import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
} from '@mui/material';

interface ContactFormDialogProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
}

const ContactFormDialog: React.FC<ContactFormDialogProps> = ({
  open,
  onClose,
  title = 'Talk with Zendi',
  description = 'Tell us what you are building. We will get back to you.',
}) => {
  const [formData, setFormData] = useState({
    fullName: '',
    businessName: '',
    emailAddress: '',
    location: '',
    businessType: '',
    systemInterest: '',
    additionalInfo: '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (e: any) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await new Promise((resolve) => setTimeout(resolve, 1200));
      setSuccess(true);
      setTimeout(() => {
        onClose();
        setFormData({
          fullName: '',
          businessName: '',
          emailAddress: '',
          location: '',
          businessType: '',
          systemInterest: '',
          additionalInfo: '',
        });
        setSuccess(false);
      }, 1800);
    } catch {
      setError('Could not send the message. Email us directly at sales@zendi.org.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle sx={{ fontWeight: 700, letterSpacing: '-0.03em', pt: 3 }}>
          {title}
        </DialogTitle>
        <DialogContent>
          {success ? (
            <Alert severity="success">Received. We will be in touch shortly.</Alert>
          ) : error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : (
            <Typography color="text.secondary" sx={{ mb: 3, fontSize: 14 }}>
              {description}
            </Typography>
          )}

          {!success && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                fullWidth
                label="Full name"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                required
                size="small"
              />
              <TextField
                fullWidth
                label="Organisation"
                name="businessName"
                value={formData.businessName}
                onChange={handleChange}
                required
                size="small"
              />
              <TextField
                fullWidth
                label="Email"
                name="emailAddress"
                type="email"
                value={formData.emailAddress}
                onChange={handleChange}
                required
                size="small"
              />
              <TextField
                fullWidth
                label="Location"
                name="location"
                value={formData.location}
                onChange={handleChange}
                required
                size="small"
                placeholder="City, country"
              />
              <FormControl fullWidth size="small">
                <InputLabel>Organisation type</InputLabel>
                <Select
                  name="businessType"
                  value={formData.businessType}
                  label="Organisation type"
                  onChange={handleSelectChange}
                  required
                >
                  {['Medical aid', 'Clinic', 'Hospital', 'Pharmacy', 'Insurer', 'Other'].map(
                    (type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    )
                  )}
                </Select>
              </FormControl>
              <FormControl fullWidth size="small">
                <InputLabel>Interest</InputLabel>
                <Select
                  name="systemInterest"
                  value={formData.systemInterest}
                  label="Interest"
                  onChange={handleSelectChange}
                  required
                >
                  <MenuItem value="AdamCore">AdamCore / diagnostic experts</MenuItem>
                  <MenuItem value="Companion">Companion</MenuItem>
                  <MenuItem value="iPOS">iPOS</MenuItem>
                  <MenuItem value="Member app">Member application</MenuItem>
                  <MenuItem value="Hardware">Hardware</MenuItem>
                  <MenuItem value="ziPOS">ziPOS</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label="How can we help?"
                name="additionalInfo"
                value={formData.additionalInfo}
                onChange={handleChange}
                multiline
                rows={3}
                size="small"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={onClose} disabled={loading}>
            {success ? 'Close' : 'Cancel'}
          </Button>
          {!success && (
            <Button type="submit" variant="contained" disabled={loading}>
              {loading ? <CircularProgress size={18} sx={{ color: 'white' }} /> : 'Send'}
            </Button>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ContactFormDialog;
